import { skipToken } from "@reduxjs/toolkit/query";
import { CircleDollarSign, ClipboardList, ImageOff, ReceiptText, Search, Sparkles, Truck } from "lucide-react";
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { FormattedNumber } from "react-intl";
import {
  OrderDetail as ApiOrderDetail,
  type SalesData as ApiSalesData,
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsCompletedOrderDetailsQuery,
} from "randmar-api-client";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Input,
  Progress,
  useChat,
} from "randmarcomps";
import { Link } from "react-router-dom";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import InlineSpinner from "../components/util/InlineSpinner";
import ManufacturerContext from "../contexts/ManufacturerContext";
import Path from "../routes/Path";
import { OrderDetail } from "../stores/document/Document";
import { RootStoreContext } from "../stores/rootStore";
import withRoot from "../withRoot";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type SalesDataField = Extract<keyof ApiSalesData, "SalesLast30Days" | "SalesLast3Months" | "SalesYearToDay">;

interface TimeframeConfig {
  id: string;
  label: string;
  days: number;
  salesField?: SalesDataField;
}

const TIMEFRAME_CONFIGS: TimeframeConfig[] = [
  { id: "30", label: "Last 30 days", days: 30, salesField: "SalesLast30Days" },
  { id: "90", label: "Last 3 months", days: 90, salesField: "SalesLast3Months" },
  { id: "365", label: "Last 12 months", days: 365, salesField: "SalesYearToDay" },
];

interface ItemPerformanceSnapshot {
  key: string;
  label: string;
  randmarSKU?: string;
  mpn?: string;
  category?: string;
  totalRevenue: number;
  totalUnits: number;
  orderCount: number;
  lastOrdered?: number;
}

type HighlightMetricFormat = "currency" | "number" | "text";

interface ItemPerformanceHighlight {
  id: string;
  label: string;
  itemLabel: string;
  itemSku?: string;
  itemCategory?: string;
  metric: number | string;
  metricFormat: HighlightMetricFormat;
  detail: string;
  helperText?: string;
}

type CompletedOrderStatusFilter = "all" | "invoice" | "shipment" | "order";
type CompletedOrderStatus = Exclude<CompletedOrderStatusFilter, "all">;

const parseDateInt = (value?: number) => {
  if (!value) return undefined;
  const stringValue = value.toString();
  if (stringValue.length !== 8) return undefined;

  const year = Number(stringValue.slice(0, 4));
  const month = Number(stringValue.slice(4, 6)) - 1;
  const day = Number(stringValue.slice(6, 8));

  if ([year, month, day].some(part => Number.isNaN(part))) {
    return undefined;
  }

  return new Date(Date.UTC(year, month, day));
};

const getLineAmount = (order: OrderDetail) => {
  const qty = order.quantity ?? 0;
  const unitPrice = order.unitPrice ?? 0;
  const extended = order.extendedPrice ?? qty * unitPrice;
  return Number.isFinite(extended) ? extended : 0;
};

const formatRelativeDate = (timestamp?: number | null) => {
  if (!timestamp) return "No recent activity";
  const diffDays = Math.floor((Date.now() - timestamp) / MS_PER_DAY);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 30) return `${diffDays} days ago`;
  const months = Math.floor(diffDays / 30);
  if (months < 12) return `${months} mo ago`;
  const years = Math.floor(months / 12);
  return `${years} yr${years > 1 ? "s" : ""} ago`;
};

const getCompletedOrderStatus = (order: OrderDetail): CompletedOrderStatus => {
  if (order.documentType === "Shipment") {
    return "shipment";
  }
  if (order.documentType === "Invoice") {
    return "invoice";
  }
  return "order";
};

function buildCompletedOrdersAssistantPrompt(options: {
  applicationId: string;
  totalLineItems: number;
  visibleLineItems: number;
  searchTerm: string;
  statusFilter: CompletedOrderStatusFilter;
  showOnlyBidOrders: boolean;
  totalAmount: number;
  totalUnits: number;
}) {
  const search = options.searchTerm.trim();
  const statusLabel = options.statusFilter === "all"
    ? "All statuses"
    : options.statusFilter.charAt(0).toUpperCase() + options.statusFilter.slice(1);
  const flags = [options.showOnlyBidOrders ? "bidsOnly" : null].filter(Boolean);

  return [
    "You are assisting with the Randmar Manufacturer Portal Completed Orders page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Total completed line items loaded: ${options.totalLineItems}`,
    `Visible line items (after filtering): ${options.visibleLineItems}`,
    `Visible totals: ${CurrencyUtils.formatNumber(options.totalAmount)} • ${new Intl.NumberFormat("en-US").format(options.totalUnits)} units`,
    `Current filters: status=${statusLabel}${search ? `, search="${search}"` : ""}${flags.length ? `, flags=${flags.join("+")}` : ""}`,
    "",
    "Help me understand what I'm seeing and suggest next actions.",
  ].join("\n");
}

const currencyFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

type HighlightItemImageProps = {
  sku?: string;
  label: string;
  cacheKey: string;
  className?: string;
};

const HighlightItemImage = ({ sku, label, cacheKey, className }: HighlightItemImageProps) => {
  const [hasError, setHasError] = useState(false);
  const src = useMemo(() => (sku ? `${Path.productImage(sku)}?v=${cacheKey}` : null), [cacheKey, sku]);
  const baseClassName =
    "flex items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-slate-50 text-[color:var(--ops-accent)]";
  const resolvedClassName = className ? `${baseClassName} ${className}` : `${baseClassName} h-16 w-16`;

  useEffect(() => {
    setHasError(false);
  }, [sku]);

  if (!sku || hasError) {
    return (
      <div className={resolvedClassName}>
        <ImageOff className="h-5 w-5 text-slate-400" aria-hidden="true" />
        <span className="sr-only">{`Image not available for ${label}`}</span>
      </div>
    );
  }

  return (
    <div className={resolvedClassName}>
      <img
        src={src ?? undefined}
        alt={label}
        onError={() => setHasError(true)}
        className="h-full w-full object-contain p-2"
      />
    </div>
  );
};

const CompletedOrderDetails = () => {
  const { applicationId } = useContext(ManufacturerContext);
  const store = useContext(RootStoreContext);
  const { sendMessageWithClear } = useChat();
  const previousTitleRef = useRef<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<CompletedOrderStatusFilter>("all");
  const [showOnlyBidOrders, setShowOnlyBidOrders] = useState(false);
  const statusMessageId = useId();

  const accountQueryArg = applicationId
    ? { routeManufacturerId: applicationId, withSpecification: true }
    : skipToken;

  const {
    data: manufacturerAccount,
    isFetching: isAccountFetching,
    refetch: refetchAccount,
  } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(accountQueryArg);

  const completedOrdersQueryArg = applicationId
    ? { routeManufacturerId: applicationId }
    : skipToken;

  const {
    data: apiCompletedOrders,
    isLoading,
    isFetching,
    isError,
    refetch: refetchCompletedOrders,
  } = useGetV4ManufacturerByRouteManufacturerIdDocumentsCompletedOrderDetailsQuery(completedOrdersQueryArg);

  const salesData = manufacturerAccount?.SalesData;
  const isRefreshing = isFetching || isAccountFetching;

  useEffect(() => {
    previousTitleRef.current = document.title;
    return () => {
      if (previousTitleRef.current) {
        document.title = previousTitleRef.current;
      }
    };
  }, []);

  useEffect(() => {
    if (!isFetching && !isAccountFetching && (apiCompletedOrders !== undefined || isError)) {
      setLastRefreshedAt(new Date());
    }
  }, [apiCompletedOrders, isAccountFetching, isError, isFetching]);

  const handleRefresh = useCallback(async () => {
    if (!applicationId) return;
    await Promise.all([refetchCompletedOrders(), refetchAccount()]);
  }, [applicationId, refetchAccount, refetchCompletedOrders]);

  const completedOrders = useMemo(() => {
    if (!apiCompletedOrders) {
      return [] as OrderDetail[];
    }
    return (apiCompletedOrders as ApiOrderDetail[]).map(OrderDetail.convertFromApi);
  }, [apiCompletedOrders]);

  const { warehousesStore } = store;

  const sortedCompletedOrders = useMemo(() => (
    completedOrders
      .slice()
      .sort(
        (a, b) =>
          (b.orderNumber || "").localeCompare(a.orderNumber || "") ||
          (a.mpn || "").localeCompare(b.mpn || "")
      )
  ), [completedOrders]);

  const filteredOrders = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();

    return sortedCompletedOrders.filter(order => {
      if (showOnlyBidOrders && !order.opportunityManufacturerNumber) {
        return false;
      }
      if (statusFilter !== "all" && getCompletedOrderStatus(order) !== statusFilter) {
        return false;
      }
      if (!normalized) {
        return true;
      }
      const locationName = warehousesStore?.getWarehouseNameFromCode(order.warehouseCode) || "";
      const haystack = [
        order.resellerName,
        order.orderNumber,
        order.poNumber,
        order.mpn,
        order.randmarSKU,
        order.warehouseCode,
        locationName,
      ]
        .filter(Boolean)
        .map(value => value!.toString().toLowerCase());

      return haystack.some(value => value.includes(normalized));
    });
  }, [searchTerm, showOnlyBidOrders, sortedCompletedOrders, statusFilter, warehousesStore]);

  const isInitialLoad = !lastRefreshedAt && (isLoading || isRefreshing) && !isError;
  const hasOrders = sortedCompletedOrders.length > 0;

  const titlePrefix = useMemo(() => {
    const parts: string[] = ["Completed orders"];

    if (statusFilter !== "all") {
      parts.push(statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1));
    }

    if (showOnlyBidOrders) {
      parts.push("Bids");
    }

    const trimmed = searchTerm.trim();
    if (trimmed) {
      const truncated = trimmed.length > 18 ? `${trimmed.slice(0, 18)}…` : trimmed;
      parts.push(`Search: ${truncated}`);
    }

    return parts.join(" · ");
  }, [searchTerm, showOnlyBidOrders, statusFilter]);

  useEffect(() => {
    if (!previousTitleRef.current) return;
    document.title = `${titlePrefix} | ${previousTitleRef.current}`;
  }, [titlePrefix]);

  const timeframeOrderStats = useMemo(() => {
    const now = Date.now();
    return TIMEFRAME_CONFIGS.reduce<Record<string, number>>((acc, { id, days }) => {
      const cutoff = now - days * MS_PER_DAY;
      let amount = 0;

      sortedCompletedOrders.forEach(order => {
        const orderDate = parseDateInt(order.orderDate ?? order.documentDate);
        if (!orderDate) return;
        if (orderDate.getTime() >= cutoff) {
          amount += getLineAmount(order);
        }
      });

      acc[id] = amount;
      return acc;
    }, {});
  }, [sortedCompletedOrders]);

  const timeframeStats = useMemo(() => {
    return TIMEFRAME_CONFIGS.map(({ id, label, salesField }) => {
      const fallbackAmount = timeframeOrderStats[id] ?? 0;
      let apiAmount: number | undefined;
      if (salesField && salesData && typeof salesData[salesField] === "number") {
        apiAmount = salesData[salesField] ?? undefined;
      }

      return {
        id,
        label,
        amount: apiAmount ?? fallbackAmount,
      };
    });
  }, [salesData, timeframeOrderStats]);

  const summaryTotals = useMemo(() => {
    return filteredOrders.reduce(
      (acc, order) => {
        acc.amount += getLineAmount(order);
        acc.units += order.quantity ?? 0;
        return acc;
      },
      { amount: 0, units: 0 }
    );
  }, [filteredOrders]);

  const lastHundredStats = useMemo(() => {
    const ordersByRecency = completedOrders
      .slice()
      .sort((a, b) => {
        const aDate = parseDateInt(a.orderDate ?? a.documentDate);
        const bDate = parseDateInt(b.orderDate ?? b.documentDate);
        if (aDate && bDate) {
          return bDate.getTime() - aDate.getTime();
        }
        if (aDate) return -1;
        if (bDate) return 1;
        return 0;
      })
      .slice(0, 100);

    return ordersByRecency.reduce((total, order) => {
      total += getLineAmount(order);
      return total;
    }, 0);
  }, [completedOrders]);

  const metricCards = useMemo(() => {
    return [
      { id: "last-100", label: "Last 100 orders", amount: lastHundredStats },
      ...timeframeStats.map(stat => ({ id: stat.id, label: stat.label, amount: stat.amount })),
    ];
  }, [lastHundredStats, timeframeStats]);

  const statusFilterOptions = useMemo(
    () => [
      { id: "all" as const, label: "All", icon: <Sparkles className="h-4 w-4" aria-hidden="true" /> },
      { id: "shipment" as const, label: "Shipments", icon: <Truck className="h-4 w-4" aria-hidden="true" /> },
      { id: "invoice" as const, label: "Invoices", icon: <ReceiptText className="h-4 w-4" aria-hidden="true" /> },
      { id: "order" as const, label: "Orders", icon: <ClipboardList className="h-4 w-4" aria-hidden="true" /> },
    ],
    []
  );

  const statusMessage = useMemo(() => {
    if (isInitialLoad) {
      return "Fetching the latest completed orders…";
    }

    if (!hasOrders && !isRefreshing) {
      return "No completed orders yet.";
    }

    const parts: string[] = [
      `Showing ${currencyFormatter.format(filteredOrders.length)} of ${currencyFormatter.format(sortedCompletedOrders.length)} completed line items`,
      `${CurrencyUtils.formatNumber(summaryTotals.amount)} total`,
      `${currencyFormatter.format(summaryTotals.units)} units`,
    ];

    if (showOnlyBidOrders) {
      parts.push("Bids only");
    }

    if (statusFilter !== "all") {
      const label = statusFilterOptions.find(option => option.id === statusFilter)?.label ?? statusFilter;
      parts.push(label);
    }

    const trimmed = searchTerm.trim();
    if (trimmed) {
      parts.push(`Search: “${trimmed.length > 24 ? `${trimmed.slice(0, 24)}…` : trimmed}”`);
    }

    return parts.join(" · ");
  }, [
    filteredOrders.length,
    hasOrders,
    isInitialLoad,
    isRefreshing,
    searchTerm,
    showOnlyBidOrders,
    sortedCompletedOrders.length,
    statusFilter,
    statusFilterOptions,
    summaryTotals.amount,
    summaryTotals.units,
  ]);

  const assistantPrompt = useMemo(() => {
    if (!applicationId) return null;
    return buildCompletedOrdersAssistantPrompt({
      applicationId,
      totalLineItems: sortedCompletedOrders.length,
      visibleLineItems: filteredOrders.length,
      searchTerm,
      statusFilter,
      showOnlyBidOrders,
      totalAmount: summaryTotals.amount,
      totalUnits: summaryTotals.units,
    });
  }, [
    applicationId,
    filteredOrders.length,
    searchTerm,
    showOnlyBidOrders,
    sortedCompletedOrders.length,
    statusFilter,
    summaryTotals.amount,
    summaryTotals.units,
  ]);

  const itemPerformanceHighlights = useMemo(() => {
    if (!filteredOrders.length) return [];

    const aggregates = new Map<string, ItemPerformanceSnapshot>();
    const buildDetailText = (item: ItemPerformanceSnapshot) => {
      const detailParts = [`${item.totalUnits} units`, `${item.orderCount} orders`];
      if (item.mpn && item.mpn !== item.label) {
        detailParts.push(`MPN ${item.mpn}`);
      }
      return detailParts.join(" • ");
    };

    filteredOrders.forEach((order, index) => {
      const key = order.mpn || order.randmarSKU || `${order.orderNumber ?? "line"}-${index}`;
      const aggregate =
        aggregates.get(key) ??
        {
          key,
          label: order.mpn || "Unnamed item",
          randmarSKU: order.randmarSKU ?? undefined,
          mpn: order.mpn ?? undefined,
          category: order.category ?? undefined,
          totalRevenue: 0,
          totalUnits: 0,
          orderCount: 0,
          lastOrdered: undefined as number | undefined,
        };

      aggregate.totalRevenue += getLineAmount(order);
      aggregate.totalUnits += order.quantity ?? 0;
      aggregate.orderCount += 1;
      if (!aggregate.category && order.category) {
        aggregate.category = order.category;
      }
      const orderDate = parseDateInt(order.orderDate ?? order.documentDate)?.getTime();
      if (orderDate && (!aggregate.lastOrdered || orderDate > aggregate.lastOrdered)) {
        aggregate.lastOrdered = orderDate;
      }

      aggregates.set(key, aggregate);
    });

    const items = Array.from(aggregates.values()).filter(item => item.totalRevenue > 0 || item.totalUnits > 0);
    if (!items.length) return [];

    const usedKeys = new Set<string>();
    const pickBy = (comparator: (a: ItemPerformanceSnapshot, b: ItemPerformanceSnapshot) => number) =>
      items
        .slice()
        .sort(comparator)
        .find(item => !usedKeys.has(item.key));

    const highlights: ItemPerformanceHighlight[] = [];

    const topRevenue = pickBy((a, b) => b.totalRevenue - a.totalRevenue);
    if (topRevenue) {
      usedKeys.add(topRevenue.key);
      highlights.push({
        id: "top-revenue",
        label: "Top revenue item",
        itemLabel: topRevenue.label,
        itemSku: topRevenue.randmarSKU,
        itemCategory: topRevenue.category,
        metric: topRevenue.totalRevenue,
        metricFormat: "currency",
        detail: buildDetailText(topRevenue),
        helperText: `Avg order ${CurrencyUtils.formatNumber(topRevenue.totalRevenue / Math.max(1, topRevenue.orderCount))}`,
      });
    }

    const topUnits = pickBy((a, b) => b.totalUnits - a.totalUnits);
    if (topUnits) {
      usedKeys.add(topUnits.key);
      highlights.push({
        id: "top-units",
        label: "Most shipped item",
        itemLabel: topUnits.label,
        itemSku: topUnits.randmarSKU,
        itemCategory: topUnits.category,
        metric: topUnits.totalUnits,
        metricFormat: "number",
        detail: buildDetailText(topUnits),
        helperText: `${CurrencyUtils.formatNumber(topUnits.totalRevenue)} total revenue`,
      });
    }

    const recentMover = pickBy((a, b) => (b.lastOrdered ?? 0) - (a.lastOrdered ?? 0));
    if (recentMover) {
      usedKeys.add(recentMover.key);
      highlights.push({
        id: "recent-mover",
        label: "Fresh momentum",
        itemLabel: recentMover.label,
        itemSku: recentMover.randmarSKU,
        itemCategory: recentMover.category,
        metric: recentMover.orderCount,
        metricFormat: "number",
        detail: buildDetailText(recentMover),
        helperText: `Last shipped ${formatRelativeDate(recentMover.lastOrdered)}`,
      });
    }

    return highlights;
  }, [filteredOrders]);

  const renderHighlightMetric = useCallback((highlight: ItemPerformanceHighlight) => {
    const metricClassName = "text-2xl font-semibold text-slate-900 tabular-nums";
    if (highlight.metricFormat === "currency" && typeof highlight.metric === "number") {
      return (
        <span className={metricClassName}>
          <FormattedNumber value={highlight.metric} {...CurrencyUtils.format} />
        </span>
      );
    }
    if (highlight.metricFormat === "number" && typeof highlight.metric === "number") {
      return (
        <span className={metricClassName}>
          <FormattedNumber value={highlight.metric} />
        </span>
      );
    }
    return <span className="text-[15px] font-semibold text-slate-700">{highlight.metric}</span>;
  }, []);

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";
  const imageCacheKey = `${lastRefreshedAt?.getTime() ?? 0}`;

  return (
    <main className={pageContainerClassName} aria-busy={isRefreshing} aria-live="polite">
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Manufacturer Portal
              </span>
              {isRefreshing && (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <InlineSpinner size="sm" decorative />
                  Syncing
                </span>
              )}
            </div>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Completed orders</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              Review past invoices and shipments, spot SKU trends, and drill into reseller activity.
            </p>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={handleRefresh}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => assistantPrompt && sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                  disabled={!assistantPrompt}
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />

            <Button asChild variant="outline" className="rounded-xl">
              <Link to={Path.orderDetails(applicationId)} aria-label="View open orders">
                View open orders
              </Link>
            </Button>
          </div>
        </div>

        <dl className="mt-6 grid gap-4 md:grid-cols-4" aria-label="Sales snapshot">
          {metricCards.map(card => (
            <div key={card.id} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</dt>
              <dd className="mt-2 text-2xl font-semibold text-slate-900 tabular-nums">
                <FormattedNumber value={card.amount} {...CurrencyUtils.format} />
              </dd>
            </div>
          ))}
        </dl>

        {isError && (
          <Alert variant="destructive" className="mt-6 rounded-2xl border border-red-100 bg-white">
            <AlertTitle className="text-slate-900">Couldn’t load completed orders</AlertTitle>
            <AlertDescription className="text-slate-700">
              Try refreshing. If the problem persists, contact support.
            </AlertDescription>
          </Alert>
        )}
      </header>

      <section className={[surfaceClassName, "space-y-4"].join(" ")} aria-label="Search and filters">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by reseller, order number, PO, MPN, SKU, or location"
              className="h-11 w-full rounded-lg border-slate-200 bg-white pl-9 text-[15px]"
              aria-label="Search completed orders"
              inputMode="search"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {statusFilterOptions.map(option => {
              const isActive = option.id === statusFilter;
              const className = [
                "rounded-xl",
                isActive ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <Button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => setStatusFilter(option.id)}
                  variant="outline"
                  size="sm"
                  className={className}
                >
                  {option.icon}
                  {option.label}
                </Button>
              );
            })}

            <Button
              type="button"
              aria-pressed={showOnlyBidOrders}
              onClick={() => setShowOnlyBidOrders(prev => !prev)}
              variant="outline"
              size="sm"
              className={[
                "rounded-xl",
                showOnlyBidOrders ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ].filter(Boolean).join(" ")}
            >
              <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
              Bids
            </Button>
          </div>
        </div>

        <p id={statusMessageId} role="status" className="text-[13px] text-slate-500" aria-live="polite">
          {statusMessage}
        </p>
      </section>

      {isInitialLoad ? (
        <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Progress />
            <p className="text-[15px] text-slate-600">Fetching the latest completed orders…</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {itemPerformanceHighlights.length > 0 && (
            <section className={[surfaceClassName, "space-y-4"].join(" ")} aria-label="Item performance highlights">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Insights</p>
                <h2 className="mt-2 text-[24px] font-semibold text-slate-900">Item performance highlights</h2>
                <p className="mt-1 text-[15px] text-slate-600">
                  Auto-surfaced from {currencyFormatter.format(filteredOrders.length)} completed line items.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {itemPerformanceHighlights.map(highlight => {
                  const destination = highlight.itemSku ? Path.product(applicationId, highlight.itemSku) : null;
                  const isInteractive = Boolean(destination);
                  const card = (
                    <Card className="h-full rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--ops-accent)]">
                      <CardContent className="flex items-start gap-4 p-6">
                        <HighlightItemImage
                          sku={highlight.itemSku}
                          label={highlight.itemLabel}
                          cacheKey={imageCacheKey}
                          className="h-16 w-16 shrink-0"
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {highlight.label}
                            </p>
                            {renderHighlightMetric(highlight)}
                          </div>
                          <div className="space-y-1">
                            <p className="truncate text-[15px] font-semibold text-slate-900">{highlight.itemLabel}</p>
                            <div className="flex flex-wrap items-center gap-2">
                              {highlight.itemSku ? (
                                <span className="text-[13px] font-mono text-slate-500">{highlight.itemSku}</span>
                              ) : null}
                              {highlight.itemCategory ? (
                                <span className="inline-flex items-center rounded-full bg-[var(--ops-accent-muted)] px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                                  {highlight.itemCategory}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <p className="text-[15px] text-slate-600">{highlight.detail}</p>
                          {highlight.helperText ? (
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {highlight.helperText}
                            </p>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  );

                  if (!isInteractive) {
                    return (
                      <div key={highlight.id} className="h-full opacity-95">
                        {card}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={highlight.id}
                      to={destination!}
                      aria-label={`View product details for ${highlight.itemLabel}`}
                      className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)] focus-visible:ring-offset-2"
                    >
                      {card}
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          <section className="space-y-4" aria-label="Completed order details table">
            <div>
              <h2 className="text-[24px] font-semibold tracking-tight text-slate-900">Order line items</h2>
              <p className="text-[15px] text-slate-600">
                Sort, paginate, and drill into reseller documents and products.
              </p>
            </div>

            <OpenOrdersGrid store={store} productOrders={filteredOrders} withHeader variant="timeline" withSearch={false} />
          </section>
        </>
      )}
    </main>
  );
};

export default withRoot(CompletedOrderDetails);
